import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FaArrowLeft, FaFileAlt, FaCheck } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import DealerVehicleForm from '../FormComponents/DealerVehicleForm';
import ApplicationDocumentUploadForm from '../FormComponents/ApplicationDocumentUploadForm';

import IdentityForm from '../FormComponents/IdentityForm';
import ContactForm from '../FormComponents/ContactForm';
import WorkForm from '../FormComponents/WorkForm';
import VehicleForm from '../FormComponents/VehicleForm';
import ConfirmationForm from '../FormComponents/ConfirmationForm';
import SuccessPage from '../FormComponents/SuccessPage';
import { validateTCNumber } from '../utils/validators';

const ApplicationPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const { currentUser } = useAuth();
    const isDealer = currentUser?.userType === 'dealer';

    const { vehicleAmount = 150000, monthlyPayment = 5000, term = 36 } = location.state || {};

    const [activeTab, setActiveTab] = useState('kimlik');
    const [progress, setProgress] = useState(20);

    const [formData, setFormData] = useState({
        // Kimlik Bilgileri
        name: '',
        surname: '',
        tcNumber: '',
        birthDate: '',
        gender: '',
        education: '',

        // İletişim Bilgileri
        phone: '',
        email: '',
        address: '',
        city: '',
        district: '',

        // İş ve Gelir Bilgileri
        occupation: '',
        workplaceName: '',
        employmentType: '',
        employmentDuration: '',
        income: '',
        workAddress: '',
        workPhone: '',

        // Araç Tercihi
        preferredVehicleBrand: '',
        preferredVehicleModel: '',
        vehicleYear: '',
        downPayment: '',
        vehicleAmount: '',
        term: 36,
        monthlyPayment: 0,

        // Notlar ve Onaylar
        notes: '',
        hasAcceptedTerms: false,
        hasAcceptedCommunication: false
    });

    const [validated, setValidated] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [error, setError] = useState('');
    const [tcError, setTcError] = useState('');
    const [documents, setDocuments] = useState([]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let processedValue = value;

        const textFields = [
            'name', 'surname', 'address', 'city', 'district',
            'occupation', 'workplaceName', 'workAddress'           
        ];

        if (textFields.includes(name)) {
            processedValue = value.toUpperCase();
        }

        if (name === 'tcNumber') {
            if (value && !/^[0-9]+$/.test(value)) {
                return;
            }

            setTcError('');

            if (value.length > 11) {
                return;
            }

            if (value.length === 11) {
                if (!validateTCNumber(value)) {
                    setTcError('Geçersiz T.C. Kimlik Numarası');
                }
            }
        }

        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : processedValue
        });
    };

    const handleNext = (currentTab) => {
        const tabsOrder = ['kimlik', 'iletisim', 'calisma', 'arac', 'belgeler', 'notlar']; // 'belgeler' eklendi
        const currentIndex = tabsOrder.indexOf(currentTab);

        if (currentIndex < tabsOrder.length - 1) {
            let isValid = true;

            if (currentTab === 'kimlik' && formData.tcNumber && !validateTCNumber(formData.tcNumber)) {
                setTcError('Geçersiz T.C. Kimlik Numarası');
                isValid = false;
            }

            if (currentTab === 'kimlik') {
                if (!formData.name || !formData.surname || !formData.tcNumber || !formData.birthDate || !formData.education) {
                    isValid = false;
                }
            } else if (currentTab === 'iletisim') {
                if (!formData.phone || !formData.email || !formData.address || !formData.city || !formData.district) {
                    isValid = false;
                }
            } else if (currentTab === 'calisma') {
                if (!formData.occupation || !formData.employmentType || !formData.income) {
                    isValid = false;
                }
            }
            // 'belgeler' step'inde validation yok (zorunlu değil)

            if (!isValid) {
                setValidated(true);
                return;
            }

            const nextTab = tabsOrder[currentIndex + 1];
            setActiveTab(nextTab);
            setProgress(Math.round((currentIndex + 2) * (100 / 6))); // 6 step oldu
            setValidated(false);
        }
    };

    const handlePrev = (currentTab) => {
        const tabsOrder = ['kimlik', 'iletisim', 'calisma', 'arac', 'belgeler', 'notlar']; // 'belgeler' eklendi
        const currentIndex = tabsOrder.indexOf(currentTab);

        if (currentIndex > 0) {
            const prevTab = tabsOrder[currentIndex - 1];
            setActiveTab(prevTab);
            setProgress(Math.round((currentIndex) * (100 / 6))); // 6 step oldu
            setValidated(false);
        }
    };

    const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.hasAcceptedTerms) {
        setError('Lütfen kullanım koşullarını ve aydınlatma metnini kabul ediniz.');
        return;
    }

    setIsSubmitting(true);
    setError('');

    try {
        const applicationData = {
            ...formData,
            vehicleAmount: isDealer ? formData.vehicleAmount : vehicleAmount,
            monthlyPayment: isDealer ? formData.monthlyPayment : monthlyPayment,
            term: isDealer ? formData.term : term,
            applicationDate: new Date().toISOString()
        };

        // DEBUG KODLARI - BUNLARI EKLEYİN
        console.log('🚀 Gönderilen application data:', applicationData);
        console.log('🔍 FormData:', formData);
        console.log('💰 Vehicle amount:', vehicleAmount);

        const response = await api.post('/applications/submit', applicationData);

        if (response.data && response.data.success) {
            console.log('Başvuru başarıyla gönderildi:', response.data);

            const applicationId = response.data.applicationId;

            // Belgeler varsa yükle
            // Belgeler varsa yükle
            if (documents.length > 0 && applicationId) {
                console.log(`📄 ${documents.length} belge yükleniyor...`);

                for (const doc of documents) {
                    try {
                        const uploadData = new FormData();
                        uploadData.append('document', doc.file);
                        uploadData.append('category', doc.category);
                        uploadData.append('title', `${doc.category} - Başvuru`);
                        uploadData.append('applicationId', applicationId);
                        uploadData.append('customerInfo', JSON.stringify({
                            name: `${formData.name} ${formData.surname}`,
                            phone: formData.phone,
                            email: formData.email,
                            tcNo: formData.tcNumber
                        }));

                        await api.post('/documents/upload', uploadData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                        });

                        console.log(`✅ Belge yüklendi: ${doc.category}`);
                    } catch (docError) {
                        console.error(`❌ Belge yükleme hatası (${doc.category}):`, docError);
                    }
                }
            }

            setIsSubmitted(true);
            } else {
                setError(response.data?.message || 'Başvuru gönderilirken bir hata oluştu');
            }
        } catch (error) {
            console.error('Başvuru hatası:', error);
                console.error('📋 Error response:', error.response?.data);
    console.error('📋 Error status:', error.response?.status);
setError('Başvuru işlenirken bir hata oluştu. Lütfen daha sonra tekrar deneyin.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoBack = () => {
        navigate('/');
    };

    if (isSubmitted) {
        return (
            <SuccessPage
                formData={formData}
                vehicleAmount={isDealer ? Number(formData.vehicleAmount) || 0 : vehicleAmount}
                monthlyPayment={isDealer ? Number(formData.monthlyPayment) || 0 : monthlyPayment}
                term={isDealer ? Number(formData.term) || 36 : term}
                handleGoBack={handleGoBack}
            />
        );
    }

    const stepTitles = {
        'kimlik': 'Kimlik Bilgileri',
        'iletisim': 'İletişim Bilgileri',
        'calisma': 'İş Bilgileri',
        'arac': 'Araç Bilgileri',
        'belgeler': 'Belge Yükleme', 
        'notlar': 'Onay ve Tamamlama'
    };

    return (
        <div className="application-page">
            {/* Header Section */}
            <section className="py-5">
                <Container>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <div>
                            <h1 className="section-title mb-2">Araç Finansmanı Başvurusu</h1>
                            <p className="lead mb-0">Senetli, taksitle araç finansmanı için başvuru formu</p>
                        </div>
                        <Button variant="outline-dark" onClick={handleGoBack} className="d-flex align-items-center">
                            <FaArrowLeft className="me-2" /> Ana Sayfa
                        </Button>
                    </div>
                </Container>
            </section>

            {/* Application Form Section */}
            <section className="py-5 bg-light">
                <Container>
                    <div className="modern-faq-item">
                        {/* Application Summary */}
                        <div className="modern-advantage-card mb-4">
                            <div className="advantage-icon-modern">
                                <FaFileAlt />
                            </div>
                            <div className="advantage-content">
                                <h5 className="advantage-title-modern">Başvuru Özeti</h5>
                                <Row className="mt-3">
                                    <Col md={4}>
                                        <strong>Araç Bedeli:</strong> ₺ {(isDealer ? (Number(formData.vehicleAmount) || 0) : vehicleAmount).toLocaleString('tr-TR')}
                                    </Col>
                                    <Col md={4}>
                                        <strong>Aylık Ödeme:</strong> ₺ {(isDealer ? (Number(formData.monthlyPayment) || 0) : monthlyPayment).toLocaleString('tr-TR')}
                                    </Col>
                                    <Col md={4}>
                                        <strong>Vade:</strong> {isDealer ? (formData.term || 36) : term} Ay
                                    </Col>
                                </Row>
                            </div>
                        </div>

                        {/* Progress Section */}
                        <div className="mb-5">
                            <h3 className="modern-faq-question text-center mb-4">
                                {stepTitles[activeTab]} ({progress}% Tamamlandı)
                            </h3>

                            <div className="progress mb-4" style={{ height: '8px' }}>
                                <div
                                    className="progress-bar"
                                    role="progressbar"
                                    style={{
                                        width: `${progress}%`,
                                        backgroundColor: 'var(--black)',
                                        transition: 'width 0.3s ease'
                                    }}
                                    aria-valuenow={progress}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                ></div>
                            </div>

                            {/* Step Indicators */}
                            <Row className="text-center">
                                {Object.entries(stepTitles).map(([key, title], index) => (
                                    <Col key={key}>
                                        <div className={`step-indicator ${activeTab === key || progress > (index + 1) * 20 ? 'active' : ''}`}>
                                            <div className="step-circle-modern">
                                                {progress > (index + 1) * 20 ? <FaCheck /> : (index + 1)}
                                            </div>
                                            <div className="step-label-modern">{title}</div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>

                        {error && (
                            <Alert variant="danger" className="mb-4">
                                {error}
                            </Alert>
                        )}

                        <Form noValidate validated={validated} onSubmit={handleSubmit}>
                            {/* Kimlik Bilgileri */}
                            <IdentityForm
                                isActive={activeTab === 'kimlik'}
                                formData={formData}
                                tcError={tcError}
                                handleChange={handleChange}
                                handleNext={() => handleNext('kimlik')}
                            />

                            {/* İletişim Bilgileri */}
                            <ContactForm
                                isActive={activeTab === 'iletisim'}
                                formData={formData}
                                handleChange={handleChange}
                                handlePrev={() => handlePrev('iletisim')}
                                handleNext={() => handleNext('iletisim')}
                            />

                            {/* İş Bilgileri */}
                            <WorkForm
                                isActive={activeTab === 'calisma'}
                                formData={formData}
                                handleChange={handleChange}
                                handlePrev={() => handlePrev('calisma')}
                                handleNext={() => handleNext('calisma')}
                            />

                            {/* Araç Bilgileri */}
                            {isDealer ? (
                                <DealerVehicleForm
                                    isActive={activeTab === 'arac'}
                                    formData={formData}
                                    handleChange={handleChange}
                                    handlePrev={() => handlePrev('arac')}
                                    handleNext={() => handleNext('arac')}
                                />
                            ) : (
                                <VehicleForm
                                    isActive={activeTab === 'arac'}
                                    formData={formData}
                                    handleChange={handleChange}
                                    handlePrev={() => handlePrev('arac')}
                                    handleNext={() => handleNext('arac')}
                                />
                            )}

                            {/* Belge Yükleme */}
                            <ApplicationDocumentUploadForm
                                isActive={activeTab === 'belgeler'}
                                documents={documents}
                                setDocuments={setDocuments}
                                customerInfo={{
                                    name: formData.name + ' ' + formData.surname,
                                    phone: formData.phone,
                                    email: formData.email,
                                    tcNo: formData.tcNumber
                                }}
                                handlePrev={() => handlePrev('belgeler')}
                                handleNext={() => handleNext('belgeler')}
                            />

                            {/* Onay ve Notlar */}
                            <ConfirmationForm
                                isActive={activeTab === 'notlar'}
                                formData={formData}
                                handleChange={handleChange}
                                handlePrev={() => handlePrev('notlar')}
                                isSubmitting={isSubmitting}
                                vehicleAmount={isDealer ? Number(formData.vehicleAmount) || 0 : vehicleAmount}
                                monthlyPayment={isDealer ? Number(formData.monthlyPayment) || 0 : monthlyPayment}
                                term={isDealer ? Number(formData.term) || 36 : term}
                            />
                        </Form>
                    </div>
                </Container>
            </section>
        </div>
    );
};

export default ApplicationPage;
